import { LoaderCircle, X } from 'lucide-react'
import { useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'
import { normalizarRut, validarRut } from '../../utils/rut'

function Campo({ label, requerido = false, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}

        {requerido && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {children}
    </div>
  )
}

function EmpresaModal({ empresa, onClose, onGuardado }) {
  const { recargarEmpresas, seleccionarEmpresa } = useEmpresa()
  const esEdicion = Boolean(empresa)

  const [razonSocial, setRazonSocial] = useState(
    empresa?.razon_social || ''
  )
  const [nombreFantasia, setNombreFantasia] = useState(
    empresa?.nombre_fantasia || ''
  )
  const [rut, setRut] = useState(empresa?.rut || '')
  const [giro, setGiro] = useState(empresa?.giro || '')
  const [direccion, setDireccion] = useState(
    empresa?.direccion || ''
  )
  const [comuna, setComuna] = useState(empresa?.comuna || '')
  const [ciudad, setCiudad] = useState(empresa?.ciudad || '')
  const [telefono, setTelefono] = useState(
    empresa?.telefono || ''
  )
  const [correo, setCorreo] = useState(empresa?.correo || '')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const guardarEmpresa = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    const razonSocialLimpia = razonSocial.trim()
    const nombreFantasiaLimpio = nombreFantasia.trim()

    if (!razonSocialLimpia) {
      setError('Debes ingresar la razón social.')
      return
    }

    if (!nombreFantasiaLimpio) {
      setError('Debes ingresar el nombre de fantasía.')
      return
    }

    if (!validarRut(rut)) {
      setError('El RUT ingresado no es válido.')
      return
    }

    const rutNormalizado = normalizarRut(rut)

    try {
      setGuardando(true)

      if (esEdicion) {
        const { data, error: errorActualizar } = await supabase
          .from('empresas')
          .update({
            razon_social: razonSocialLimpia,
            nombre_fantasia: nombreFantasiaLimpio,
            rut: rutNormalizado || null,
            giro: giro.trim() || null,
            direccion: direccion.trim() || null,
            comuna: comuna.trim() || null,
            ciudad: ciudad.trim() || null,
            telefono: telefono.trim() || null,
            correo: correo.trim() || null,
          })
          .eq('id', empresa.id)
          .select()
          .single()

        if (errorActualizar) {
          throw errorActualizar
        }

        onGuardado?.(data)
      } else {
        const { data, error: errorFuncion } =
          await supabase.functions.invoke('crear-empresa', {
            body: {
              razon_social: razonSocialLimpia,
              nombre_fantasia: nombreFantasiaLimpio,
              rut: rutNormalizado,
              giro: giro.trim(),
              direccion: direccion.trim(),
              comuna: comuna.trim(),
              ciudad: ciudad.trim(),
              telefono: telefono.trim(),
              correo: correo.trim(),
            },
          })

        if (errorFuncion) {
          throw errorFuncion
        }

        if (data?.error) {
          throw new Error(data.error)
        }

        await recargarEmpresas()
        seleccionarEmpresa(data.empresa.id)

        onGuardado?.(data.empresa)
      }
    } catch (errorGuardado) {
      console.error('Error al guardar empresa:', errorGuardado)

      if (errorGuardado.code === '23505') {
        setError('Ya existe una empresa con ese RUT.')
      } else {
        setError(
          errorGuardado.message ||
            'No fue posible guardar la empresa.'
        )
      }
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            {esEdicion ? 'Editar empresa' : 'Nueva empresa'}
          </h2>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="max-h-[75vh] space-y-5 overflow-y-auto pr-1"
          onSubmit={guardarEmpresa}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Campo label="Razón social" requerido>
                <input
                  type="text"
                  value={razonSocial}
                  onChange={(event) =>
                    setRazonSocial(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>
            </div>

            <Campo label="Nombre de fantasía" requerido>
              <input
                type="text"
                value={nombreFantasia}
                onChange={(event) =>
                  setNombreFantasia(event.target.value)
                }
                disabled={guardando}
                className={estiloInput}
              />
            </Campo>

            <Campo label="RUT">
              <input
                type="text"
                value={rut}
                onChange={(event) => setRut(event.target.value)}
                placeholder="12.345.678-9"
                disabled={guardando}
                className={estiloInput}
              />
            </Campo>

            <Campo label="Giro">
              <input
                type="text"
                value={giro}
                onChange={(event) => setGiro(event.target.value)}
                disabled={guardando}
                className={estiloInput}
              />
            </Campo>

            <Campo label="Teléfono">
              <input
                type="text"
                value={telefono}
                onChange={(event) =>
                  setTelefono(event.target.value)
                }
                disabled={guardando}
                className={estiloInput}
              />
            </Campo>

            <div className="sm:col-span-2">
              <Campo label="Dirección">
                <input
                  type="text"
                  value={direccion}
                  onChange={(event) =>
                    setDireccion(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>
            </div>

            <Campo label="Comuna">
              <input
                type="text"
                value={comuna}
                onChange={(event) =>
                  setComuna(event.target.value)
                }
                disabled={guardando}
                className={estiloInput}
              />
            </Campo>

            <Campo label="Ciudad">
              <input
                type="text"
                value={ciudad}
                onChange={(event) =>
                  setCiudad(event.target.value)
                }
                disabled={guardando}
                className={estiloInput}
              />
            </Campo>

            <div className="sm:col-span-2">
              <Campo label="Correo">
                <input
                  type="email"
                  value={correo}
                  onChange={(event) =>
                    setCorreo(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default EmpresaModal
